import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { IntegrationInstallationId, IntegrationType, StatisticType, StatisticUnit } from '@/types';
import {
    SummaryViewPropertyBase,
    StatusSummaryDataAPI,
    SUPPORTED_STATISTIC_TYPES,
    TableMetric,
    SummaryViewRenderContext,
} from '@/types/summaryView';
import {
    isMultiInstanceArchetypeProperty,
    getSummaryViewPropertyLabel,
    isArchetypeProperty,
    isStatisticProperty,
    getStatisticFromProperty,
} from '@/helpers/summaryViewHelpers';
import {
    StatusCell,
    NoBuildsCellComponent,
    TableDateCell,
    DurationCell,
    EmptyCell,
    HealthMetricCell,
    getBackgroundColorForHealthMetric,
    ReleaseStepCell,
    lightBackgroundColorForStatus,
    shouldShowStepEmptyState,
    IntegrationMetricCell,
    DateRangeFilterDropdown,
    dateRangeFilterIcon,
} from './OverviewTableCellComponents';
import { ReleasePilotWidget } from '@/components/appHome/widgets/ReleasePilotWidget';
import { FeatureCompletionWidget } from '@/components/appHome/widgets/FeatureCompletionWidget';
import { PhasedReleaseChartBase, AdoptionRateWidget } from '@/components/appHome/widgets/LiveReleaseChartWidget';
import AppStoreBuildComponent from '@/components/appStoreBuilds/AppStoreBuildComponent';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { displayStatValue, getStabilityInfos } from '@/helpers';
import { antGrey } from '@/components/styles/globalStyles';
import Space from 'antd/lib/space';
import { dayjs } from '@/extendedDayjs';
import { displayReleaseOverviewStatisticLabel } from '@/components/StatCard';

const getStabilityIntegrationTypesAcrossBuilds = (builds: any): IntegrationType[] => {
    if (!builds || !Array.isArray(builds)) return [];
    const seen = new Set<IntegrationType>();
    const ordered: IntegrationType[] = [];
    for (const build of builds) {
        for (const info of getStabilityInfos(build)) {
            if (!info.integrationId || seen.has(info.integrationId)) continue;
            seen.add(info.integrationId);
            ordered.push(info.integrationId);
        }
    }
    return ordered;
};

const IntegrationMetricCellMultiIcon: React.FC<{ value: string; integrationTypes: IntegrationType[] }> = ({
    value,
    integrationTypes,
}) => (
    <Space size={8} align="center">
        <Space size={4}>
            {integrationTypes.map((type) => {
                const [iconSrc, iconAlt] = iconAndAltForIntegrationType(type);
                if (!iconSrc) return null;
                return <img key={type} src={iconSrc} alt={iconAlt} width={16} height={16} />;
            })}
        </Space>
        <Text>{value}</Text>
    </Space>
);

type StabilityMetricValue = number | Record<IntegrationInstallationId, number | null | undefined>;

const isStabilityMetricValueByIntegration = (
    value: StabilityMetricValue,
): value is Record<IntegrationInstallationId, number | null | undefined> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

const renderStabilityMetricCell = (
    value: StabilityMetricValue,
    statisticType: StatisticType,
    builds: any,
): React.ReactNode => {
    if (!isStabilityMetricValueByIntegration(value)) {
        const formattedValue = displayStatValue(statisticType, value, StatisticUnit.Percent);
        const integrationTypes = getStabilityIntegrationTypesAcrossBuilds(builds);
        if (integrationTypes.length <= 1) {
            const [iconSrc, iconAlt] = iconAndAltForIntegrationType(integrationTypes[0]);
            return <IntegrationMetricCell value={formattedValue.toString()} iconSrc={iconSrc} iconAlt={iconAlt} />;
        }
        return <IntegrationMetricCellMultiIcon value={formattedValue.toString()} integrationTypes={integrationTypes} />;
    }

    const rows = Object.entries(value).filter(([, metricValue]) => metricValue != null);
    if (!rows.length) return <EmptyCell />;

    return (
        <Space direction="vertical" size={4}>
            {rows.map(([integrationInstallationId, metricValue]) => {
                const integrationType = integrationInstallationId.split(':')[0] as IntegrationType;
                const [iconSrc, iconAlt] = iconAndAltForIntegrationType(integrationType);
                const formattedValue = displayStatValue(statisticType, metricValue, StatisticUnit.Percent);
                return (
                    <IntegrationMetricCell
                        key={integrationInstallationId}
                        value={formattedValue.toString()}
                        iconSrc={iconSrc}
                        iconAlt={iconAlt}
                    />
                );
            })}
        </Space>
    );
};

const createMetric = (
    key: string,
    renderContent: (data: any, context: SummaryViewRenderContext) => React.ReactNode,
    customTitle?: string,
): TableMetric => ({
    key,
    title: customTitle || getSummaryViewPropertyLabel(key),
    renderCell: (context) => {
        const data = context.row[key];
        if (!data && data !== 0) return <EmptyCell />;
        return renderContent(data, context);
    },
});

const createDateMetric = (key: string): TableMetric => ({
    ...createMetric(key, (data, { currentAppId }) => <TableDateCell date={data} appId={currentAppId} />),
    filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <DateRangeFilterDropdown setSelectedKeys={setSelectedKeys} confirm={confirm} clearFilters={clearFilters} />
    ),
    filterIcon: dateRangeFilterIcon,
    onFilter: (value, row) => {
        const date = row[key];
        if (!date) return true;
        try {
            const [start, end] = JSON.parse(value as string);
            return dayjs(date).isBetween(dayjs(start), dayjs(end), 'day', '[]');
        } catch {
            return true;
        }
    },
});

const createArchetypeMetric = (instanceKey: string, propertyKey: string): TableMetric => ({
    key: instanceKey,
    title: getSummaryViewPropertyLabel(propertyKey),
    renderCell: ({ row }) => {
        const stepData = row[instanceKey];
        return <ReleaseStepCell stepData={stepData} />;
    },
    getCellStyle: (row) => {
        const stepData = row[instanceKey];
        if (stepData && stepData.status && !shouldShowStepEmptyState(stepData)) {
            return { backgroundColor: lightBackgroundColorForStatus(stepData.status) };
        }
        return {};
    },
});

const createStatisticMetric = (propertyKey: string, statisticType: StatisticType, sampleStat: any): TableMetric => {
    const isDuration =
        statisticType === StatisticType.WaitingForReviewDuration || statisticType === StatisticType.InReviewDuration;
    const statTitle = displayReleaseOverviewStatisticLabel(statisticType, sampleStat?.additionalData);

    return {
        key: propertyKey,
        title: statTitle || displayReleaseOverviewStatisticLabel(statisticType),
        renderCell: ({ row }) => {
            const data = row[propertyKey];
            if (!data || data.value === undefined) return <EmptyCell />;

            if (isDuration) {
                const durationText = dayjs.duration(data.value, 'seconds').humanize();
                return <DurationCell durationText={durationText} />;
            }

            const formattedValue = displayStatValue(statisticType, data.value, data.unit);
            return (
                <HealthMetricCell
                    value={formattedValue.toString()}
                    change={data.change}
                    changeStatus={data.changeStatus}
                />
            );
        },
        getCellStyle: (row) => {
            if (isDuration) return {};
            const data = row[propertyKey];
            if (data && data.changeStatus) {
                return { backgroundColor: getBackgroundColorForHealthMetric(data.changeStatus) };
            }
            return {};
        },
    };
};

export interface SummaryViewMetricDiscovery {
    multiInstanceKeysByProperty: Record<string, string[]>;
    sampleStatisticsByProperty: Record<string, any>;
}

export const createSummaryViewMetrics = (
    selectedPropertyKeys: string[],
    discovery: SummaryViewMetricDiscovery = {
        multiInstanceKeysByProperty: {},
        sampleStatisticsByProperty: {},
    },
): TableMetric[] => {
    const baseMetrics: TableMetric[] = [
        {
            key: SummaryViewPropertyBase.WorkflowRunVersion,
            title: getSummaryViewPropertyLabel(SummaryViewPropertyBase.WorkflowRunVersion),
            renderCell: ({ row }) => <Text code>{row.version}</Text>,
        },
        createMetric(SummaryViewPropertyBase.Status, (data: StatusSummaryDataAPI, { row, compact }) => {
            const rolloutArchetype = row['Archetype:rollout'];
            return (
                <StatusCell
                    type={data.type}
                    status={data.status}
                    overviewDisplayStatus={data.overviewDisplayStatus}
                    rolloutExtraData={rolloutArchetype?.extraData}
                    compact={compact}
                />
            );
        }),
        createMetric(SummaryViewPropertyBase.Builds, (builds) => {
            if (!builds || !Array.isArray(builds) || builds.length === 0) {
                return <NoBuildsCellComponent />;
            }

            const visibleBuilds = builds.slice(0, 2);
            const remainingCount = builds.length - visibleBuilds.length;

            return (
                <Space size={4} wrap={false}>
                    {visibleBuilds.map((build) => (
                        <AppStoreBuildComponent
                            key={build?.appStoreBuild?.id ?? build?.appStoreBuild?.buildNumber}
                            {...build}
                        />
                    ))}
                    {remainingCount > 0 && (
                        <Text style={{ color: antGrey.grey6, fontSize: 12 }}>+{remainingCount} more</Text>
                    )}
                </Space>
            );
        }),
        {
            key: SummaryViewPropertyBase.ReleasePilot,
            title: getSummaryViewPropertyLabel(SummaryViewPropertyBase.ReleasePilot),
            renderCell: (context) => {
                const data = context.row[SummaryViewPropertyBase.ReleasePilot];
                const destination = context.appsById[context.row.destinationId];
                const appId = destination?.appId || context.currentAppId;
                return <ReleasePilotWidget appId={appId} releasePilotId={data} showTitle={false} />;
            },
        },
        createMetric(SummaryViewPropertyBase.FeatureCompletePercent, (data) => {
            if (!data) return <EmptyCell />;
            return <FeatureCompletionWidget featuresStep={data} compact />;
        }),
        createDateMetric(SummaryViewPropertyBase.TargetKickoffDates),
        createDateMetric(SummaryViewPropertyBase.TargetSubmitDates),
        createDateMetric(SummaryViewPropertyBase.TargetReleaseDates),
        createDateMetric(SummaryViewPropertyBase.KickedOffDate),
        createDateMetric(SummaryViewPropertyBase.SubmittedDate),
        createDateMetric(SummaryViewPropertyBase.ReleasedAtDate),
        createMetric(SummaryViewPropertyBase.PhasedRelease, (phasedRelease) => {
            if (!phasedRelease) return <EmptyCell />;

            return (
                <PhasedReleaseChartBase
                    percentage={phasedRelease.phasedReleasePercent ?? 0}
                    dayNumber={phasedRelease.dayNumber ?? 0}
                    daysToPercent={phasedRelease.daysToPercentSchedule}
                    isAutomatedPhasedRollout={phasedRelease.isAutomatedPhasedRollout ?? false}
                    wasHalted={phasedRelease.isPaused ?? false}
                    wasAccelerated={phasedRelease.isAccelerated ?? false}
                    compact={true}
                />
            );
        }),
        createMetric(SummaryViewPropertyBase.AdoptionRate, (builds) => {
            if (!builds || !Array.isArray(builds) || builds.length === 0) return <EmptyCell />;

            const selectedBuild =
                builds.find((build) => getStabilityInfos(build).some((info) => info.sessionAdoption != null)) ??
                builds[0];
            const stabilityInfos = getStabilityInfos(selectedBuild).filter((info) => info.sessionAdoption != null);

            if (stabilityInfos.length === 0) return <EmptyCell />;

            return (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {stabilityInfos.map((info, idx) => {
                        const [stabIcon, stabAlt] = iconAndAltForIntegrationType(info.integrationId);
                        return (
                            <AdoptionRateWidget
                                key={`${info.integrationId}-${info.buildIdentifier ?? idx}`}
                                sessionAdoption={info.sessionAdoption}
                                stabIcon={stabIcon}
                                stabAlt={stabAlt}
                                compact
                            />
                        );
                    })}
                </Space>
            );
        }),
        createMetric(SummaryViewPropertyBase.CrashFreeUsers, (value, context) => {
            const builds = context.row[SummaryViewPropertyBase.AdoptionRate];
            return renderStabilityMetricCell(value, StatisticType.CrashFreeUsers, builds);
        }),
        createMetric(SummaryViewPropertyBase.CrashFreeSessions, (value, context) => {
            const builds = context.row[SummaryViewPropertyBase.AdoptionRate];
            return renderStabilityMetricCell(value, StatisticType.CrashFreeSessions, builds);
        }),
    ];

    const allMetrics: TableMetric[] = [...baseMetrics];

    selectedPropertyKeys.forEach((propertyKey) => {
        if (isArchetypeProperty(propertyKey)) {
            const useInstances = isMultiInstanceArchetypeProperty(propertyKey);
            const instanceKeys = useInstances ? [...(discovery.multiInstanceKeysByProperty[propertyKey] ?? [])] : [];

            if (!useInstances || instanceKeys.length === 0) {
                instanceKeys.push(propertyKey);
            }

            instanceKeys.forEach((instanceKey) => {
                allMetrics.push(createArchetypeMetric(instanceKey, propertyKey));
            });
        }

        if (isStatisticProperty(propertyKey)) {
            const statisticType = getStatisticFromProperty(propertyKey) as StatisticType | null;
            if (!statisticType || !SUPPORTED_STATISTIC_TYPES.includes(statisticType)) {
                return;
            }
            const sampleStat = discovery.sampleStatisticsByProperty[propertyKey];
            allMetrics.push(createStatisticMetric(propertyKey, statisticType, sampleStat));
        }
    });

    const alwaysVisibleMetrics = [SummaryViewPropertyBase.WorkflowRunVersion];

    return allMetrics.filter((metric) => {
        if (alwaysVisibleMetrics.includes(metric.key as SummaryViewPropertyBase)) return true;
        if (selectedPropertyKeys.includes(metric.key)) return true;
        return selectedPropertyKeys.some((selectedKey) => metric.key.startsWith(`${selectedKey}:`));
    });
};
